<script>
import { ref, useId, watch, nextTick } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters, mapActions } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SendWahaEventDialog from '../conversation/SendWahaEventDialog.vue';
import SendLocationDialog from '../conversation/SendLocationDialog.vue';
import ComposerUserActions from './ComposerUserActions.vue';
import ComposerWriteAssist from './ComposerWriteAssist.vue';
import { isWahaApiInbox } from 'dashboard/helper/wahaInbox';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';

export default {
  name: 'ReplyBottomPanel',
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    SendWahaEventDialog,
    SendLocationDialog,
    ComposerUserActions,
    ComposerWriteAssist,
  },
  mixins: [inboxMixin],
  props: {
    compactMobile: { type: Boolean, default: false },
    compactDesktop: { type: Boolean, default: false },
    modernComposer: { type: Boolean, default: false },
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'mobileMode',
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'executeCopilotAction',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);
    const mobileActionsOpen = ref(false);
    const mobileActionsPanel = ref(null);
    const mobileActionsId = useId();
    const mobileMoreButton = ref(null);
    const { captainTasksEnabled } = useCaptain();
    const writeAssistOpen = ref(false);
    const writeAssistRef = ref(null);
    const closeMobileActions = (restoreFocus = false) => {
      mobileActionsOpen.value = false;
      writeAssistOpen.value = false;
      if (restoreFocus) nextTick(() => mobileMoreButton.value?.$el?.focus());
    };
    const onActionsEscape = () => {
      if (writeAssistOpen.value) {
        writeAssistRef.value?.closeWithFocus?.();
        return;
      }
      closeMobileActions(true);
    };
    const onMobileActionClick = event => {
      if (
        event.target.closest('button') &&
        !event.target.closest('[data-composer-nested-menu]')
      )
        closeMobileActions();
    };
    watch(
      () => props.compactMobile,
      compact => {
        if (!compact) closeMobileActions();
      }
    );
    watch(
      () => props.conversationId,
      () => closeMobileActions()
    );
    watch(mobileActionsOpen, isOpen => {
      if (!isOpen) writeAssistOpen.value = false;
    });
    onClickOutside(
      mobileActionsPanel,
      () => {
        mobileActionsOpen.value = false;
      },
      {
        ignore: ['[data-testid="mobile-composer-more"]'],
      }
    );
    const { isSmallScreen } = useSmallScreen();

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
      mobileActionsOpen,
      mobileActionsPanel,
      mobileActionsId,
      mobileMoreButton,
      closeMobileActions,
      onActionsEscape,
      onMobileActionClick,
      isSmallScreen,
      captainTasksEnabled,
      writeAssistOpen,
      writeAssistRef,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
      uiSettings: 'getUISettings',
    }),
    // SiteUp — Modo Foco: botões da barra que o usuário escolheu ocultar
    // (ui_settings.conversation_reply_hidden_items).
    replyHiddenItems() {
      const hidden = this.uiSettings?.conversation_reply_hidden_items;
      return Array.isArray(hidden) ? hidden : [];
    },
    useActionsMenu() {
      return this.compactMobile || this.modernComposer;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
        '!contents': this.compactMobile || this.compactDesktop,
        '!px-3 !pb-3 !pt-1 items-center':
          this.modernComposer && !this.compactMobile && !this.compactDesktop,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      if (this.replyHiddenItems.includes('attach')) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.replyHiddenItems.includes('audio_recorder')) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderControlLabel() {
      if (this.recordingAudioState === 'playing') {
        return this.$t('CONVERSATION.REPLYBOX.PAUSE_AUDIO_PLAYBACK');
      }
      if (['paused', 'stopped'].includes(this.recordingAudioState)) {
        return this.$t('CONVERSATION.REPLYBOX.PLAY_AUDIO_RECORDING');
      }
      return this.$t('CONVERSATION.REPLYBOX.STOP_AUDIO_RECORDING');
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      if (this.replyHiddenItems.includes('signature')) return false;
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      if (this.replyHiddenItems.includes('insert_article')) return false;
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    showWahaEventButton() {
      if (this.isEditorDisabled || this.isOnPrivateNote) return false;
      if (this.replyHiddenItems.includes('waha_event')) return false;
      return this.isWahaComposerInbox;
    },
    showLocationButton() {
      if (this.isEditorDisabled || this.isOnPrivateNote) return false;
      if (this.replyHiddenItems.includes('location')) return false;
      return this.isWahaComposerInbox;
    },
    isWahaComposerInbox() {
      const attrs = this.inbox?.additional_attributes || {};
      return isWahaApiInbox(this.inbox) || Boolean(attrs.waha_session);
    },
  },
  mounted() {
    ActiveStorage.start();
    emitter.on(BUS_EVENTS.MOBILE_COMPOSER_ACTION, this.onMobileComposerAction);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.MOBILE_COMPOSER_ACTION, this.onMobileComposerAction);
  },
  methods: {
    ...mapActions(['addMessage']),
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    openWahaEventDialog() {
      this.$refs.wahaEventDialog?.open();
    },
    openLocationDialog() {
      this.$refs.locationDialog?.open();
    },
    onWahaEventSent(message) {
      if (message) this.addMessage(message);
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    },
    onLocationSent(message) {
      if (message) this.addMessage(message);
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    },
    onMobileComposerAction(action) {
      if (action === 'signature') this.toggleMessageSignature();
      if (action === 'waha_event') this.openWahaEventDialog();
      if (action === 'location') this.openLocationDialog();
    },
    onWriteAssistAction(action, data) {
      this.$emit('executeCopilotAction', action, data);
    },
    onWriteAssistCloseMenu() {
      this.closeMobileActions();
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 px-2 py-1"
    :class="wrapClass"
    :data-testid="
      isSmallScreen ? 'mobile-composer-bar' : 'desktop-composer-bar'
    "
  >
    <NextButton
      v-if="useActionsMenu"
      ref="mobileMoreButton"
      :icon="mobileActionsOpen ? 'i-ph-x' : 'i-ph-plus'"
      :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
      :aria-expanded="mobileActionsOpen"
      :aria-controls="mobileActionsId"
      class="!size-11 !p-0 col-start-1 row-start-1"
      :class="{
        '[&>span]:!size-6': modernComposer,
        '!row-start-2': compactDesktop,
      }"
      color="slate"
      variant="ghost"
      data-testid="mobile-composer-more"
      @click="mobileActionsOpen = !mobileActionsOpen"
      @keydown.esc.stop="onActionsEscape"
    />
    <div
      v-show="!useActionsMenu || mobileActionsOpen"
      :id="mobileActionsId"
      ref="mobileActionsPanel"
      :class="{
        'absolute bottom-full inset-x-0 mb-3 !p-2 !flex-col !items-stretch !gap-1 !flex-nowrap !overflow-y-auto max-h-[min(24rem,50dvh)] rounded-2xl border border-n-weak bg-n-solid-1 shadow-lg z-30 [&_button]:min-h-11 [&_button]:min-w-11 [&_button]:!justify-start [&_button]:!rounded-lg [&_button]:!bg-transparent [&_button]:hover:!bg-n-alpha-2 [&>button]:w-full [&>button]:justify-start [&_.file-uploads]:w-full [&_.file-uploads_button]:w-full [&_.file-uploads_button]:justify-start':
          useActionsMenu,
        'md:!inset-x-auto md:left-3 md:w-72': modernComposer && !compactMobile,
      }"
      class="left-wrap no-scrollbar min-w-0 flex-1 overflow-x-auto [&>*]:shrink-0"
      @keydown.esc.stop="onActionsEscape"
      @click="onMobileActionClick"
    >
      <NextButton
        v-if="isSmallScreen && useActionsMenu && !writeAssistOpen"
        :label="
          $t(
            isOnPrivateNote
              ? 'CONVERSATION.REPLYBOX.REPLY'
              : 'CONVERSATION.REPLYBOX.PRIVATE_NOTE'
          )
        "
        :icon="isOnPrivateNote ? 'i-ph-chat-circle' : 'i-ph-lock'"
        color="slate"
        variant="ghost"
        class="min-h-11"
        @click="
          $emit('mobileMode', isOnPrivateNote ? 'reply' : 'note');
          mobileActionsOpen = false;
        "
      />
      <ComposerWriteAssist
        v-if="useActionsMenu && captainTasksEnabled"
        ref="writeAssistRef"
        v-model:open="writeAssistOpen"
        :conversation-id="conversationId"
        :has-content="hasContent"
        :editor-content="message"
        :disabled="disabled"
        :is-editor-disabled="isEditorDisabled"
        @execute-copilot-action="onWriteAssistAction"
        @close-menu="onWriteAssistCloseMenu"
      />
      <template v-if="!writeAssistOpen">
        <div
          v-if="!isEditorDisabled && !isOnPrivateNote"
          class="contents"
          data-composer-nested-menu
        >
          <ComposerUserActions
            :conversation-id="conversationId"
            :show-label="useActionsMenu"
          />
        </div>
        <NextButton
          v-if="
            !modernComposer &&
            !isEditorDisabled &&
            !replyHiddenItems.includes('emoji')
          "
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          :label="
            useActionsMenu
              ? $t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')
              : undefined
          "
          :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley-sticker"
          slate
          faded
          sm
          @click="toggleEmojiPicker"
        />
        <FileUpload
          v-if="showAttachButton"
          ref="uploadRef"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          data-mobile-composer-control="attach"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <label for="conversationAttachment" class="sr-only">
            {{ $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON') }}
          </label>
          <NextButton
            v-if="showAttachButton"
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            :label="
              useActionsMenu
                ? $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')
                : undefined
            "
            :aria-label="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="i-ph-paperclip"
            slate
            faded
            sm
          />
        </FileUpload>
        <NextButton
          v-if="showAudioRecorderButton && !useActionsMenu"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
          :label="
            useActionsMenu
              ? $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
              : undefined
          "
          :aria-label="
            isRecordingAudio
              ? $t('CONVERSATION.REPLYBOX.STOP_AUDIO_RECORDING')
              : $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
          "
          :icon="
            !isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'
          "
          slate
          faded
          sm
          @click="toggleAudioRecorder"
        />
        <NextButton
          v-if="showAudioPlayStopButton && !useActionsMenu"
          :icon="audioRecorderPlayStopIcon"
          :aria-label="audioRecorderControlLabel"
          slate
          faded
          sm
          :label="recordingAudioDurationText"
          @click="toggleAudioRecorderPlayPause"
        />
        <NextButton
          v-if="showMessageSignatureButton"
          v-tooltip.top-end="signatureToggleTooltip"
          :label="useActionsMenu ? signatureToggleTooltip : undefined"
          :aria-label="signatureToggleTooltip"
          icon="i-ph-signature"
          slate
          faded
          sm
          @click="toggleMessageSignature"
        />

        <NextButton
          v-if="showQuotedReplyToggle"
          v-tooltip.top-end="quotedReplyToggleTooltip"
          :label="useActionsMenu ? quotedReplyToggleTooltip : undefined"
          :aria-label="quotedReplyToggleTooltip"
          icon="i-ph-quotes"
          :variant="quotedReplyEnabled ? 'solid' : 'faded'"
          color="slate"
          sm
          :aria-pressed="quotedReplyEnabled"
          @click="$emit('toggleQuotedReply')"
        />
        <NextButton
          v-if="showWahaEventButton"
          v-tooltip.top-end="$t('CONVERSATION.WAHA_EVENT.TIP')"
          :label="
            useActionsMenu ? $t('CONVERSATION.WAHA_EVENT.TIP') : undefined
          "
          :aria-label="$t('CONVERSATION.WAHA_EVENT.TIP')"
          icon="i-ph-calendar-plus"
          slate
          faded
          sm
          data-testid="waha-event-composer-trigger"
          @click="openWahaEventDialog"
        />
        <SendWahaEventDialog
          ref="wahaEventDialog"
          :conversation-id="conversationId"
          @sent="onWahaEventSent"
        />
        <NextButton
          v-if="showLocationButton"
          v-tooltip.top-end="$t('CONVERSATION.LOCATION.TIP')"
          :label="useActionsMenu ? $t('CONVERSATION.LOCATION.TIP') : undefined"
          :aria-label="$t('CONVERSATION.LOCATION.TIP')"
          icon="i-ph-map-pin"
          slate
          faded
          sm
          data-testid="location-composer-trigger"
          @click="openLocationDialog"
        />
        <SendLocationDialog
          ref="locationDialog"
          :conversation-id="conversationId"
          @sent="onLocationSent"
        />
        <NextButton
          v-if="enableWhatsAppTemplates"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          :label="
            useActionsMenu
              ? $t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')
              : undefined
          "
          :aria-label="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableContentTemplates"
          v-tooltip.top-end="
            $t('CONVERSATION.HEADER.MOBILE.MENU_CONTENT_TEMPLATES')
          "
          :label="
            useActionsMenu
              ? $t('CONVERSATION.HEADER.MOBILE.MENU_CONTENT_TEMPLATES')
              : undefined
          "
          :aria-label="$t('CONVERSATION.HEADER.MOBILE.MENU_CONTENT_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectContentTemplate')"
        />
        <VideoCallButton
          v-if="
            (isAWebWidgetInbox || isAPIInbox) &&
            !isOnPrivateNote &&
            !isEditorDisabled &&
            !replyHiddenItems.includes('video_call')
          "
          :show-label="useActionsMenu"
          :conversation-id="conversationId"
        />
        <transition name="modal-fade">
          <div
            v-show="uploadRef && uploadRef.dropActive"
            class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
          >
            <span class="i-ph-cloud-arrow-up size-10" />
            <h4 class="text-2xl break-words text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
            </h4>
          </div>
        </transition>
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="
            $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          "
          :label="
            useActionsMenu
              ? $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
              : undefined
          "
          :aria-label="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
          icon="i-ph-article-ny-times"
          slate
          faded
          sm
          @click="toggleInsertArticle"
        />
      </template>
    </div>
    <div
      class="right-wrap shrink-0"
      :class="{
        'col-start-3 row-start-1 gap-1': compactMobile,
        'col-start-3 row-start-2 gap-1': compactDesktop,
        'ml-auto !gap-2': modernComposer && !compactMobile,
      }"
    >
      <NextButton
        v-if="
          modernComposer &&
          !isEditorDisabled &&
          !replyHiddenItems.includes('emoji')
        "
        v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        data-testid="composer-emoji"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley"
        color="slate"
        variant="ghost"
        class="!size-11 !p-0 !rounded-full [&>span]:!size-6"
        @click="toggleEmojiPicker"
      />
      <NextButton
        v-if="useActionsMenu && showAudioPlayStopButton"
        data-testid="composer-audio-playback"
        :icon="audioRecorderPlayStopIcon"
        :aria-label="audioRecorderControlLabel"
        :label="recordingAudioDurationText"
        color="slate"
        variant="faded"
        class="!min-h-11 !rounded-xl"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="useActionsMenu && showAudioRecorderButton"
        data-testid="mobile-composer-audio"
        :aria-label="
          isRecordingAudio
            ? $t('CONVERSATION.REPLYBOX.CANCEL_AUDIO_RECORDING')
            : $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
        "
        :icon="isRecordingAudio ? 'i-ph-x' : 'i-ph-microphone'"
        color="slate"
        variant="ghost"
        class="!size-11 !p-0 !rounded-full"
        :class="{ '[&>span]:!size-6': modernComposer }"
        @click="toggleAudioRecorder"
      />
      <NextButton
        data-mobile-composer-control="send"
        :label="
          compactMobile
            ? undefined
            : modernComposer
              ? $t(
                  isNote
                    ? 'CONVERSATION.REPLYBOX.CREATE'
                    : 'CONVERSATION.REPLYBOX.SEND'
                )
              : sendButtonText
        "
        :icon="useActionsMenu ? 'i-ph-arrow-up' : undefined"
        :aria-label="
          useActionsMenu
            ? $t(
                isNote
                  ? 'CONVERSATION.REPLYBOX.CREATE'
                  : 'CONVERSATION.REPLYBOX.SEND'
              )
            : undefined
        "
        :class="{
          '!size-11 !p-0 !rounded-full': compactMobile,
          '!min-h-11 !rounded-xl !px-4': modernComposer && !compactMobile,
        }"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex;
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
